import { useMemo } from "react";
import markets from "../../lib/markets";
import { getBacktestBalance } from "../../utils/backtestAccountUtils";

const TradingStatisticsPanel = ({ trades, theme, showGross = false }) => {
  const stats = useMemo(() => {
    if (!trades || trades.length === 0) {
      return {
        profitability: 0,
        avgWinningTrade: 0,
        avgLosingTrade: 0,
        totalTrades: 0,
        avgRRR: 0,
        winRate: 0,
        lossRate: 0,
        profitFactor: 0,
        bestTrade: 0,
        worstTrade: 0,
        longWon: 0,
        shortWon: 0,
        grossProfit: 0,
        grossLoss: 0,
        peakBalance: 0,
        maxDrawdown: 0,
        maxDrawdownPercent: 0,
        avgPipsPerTrade: 0,
        avgPipsWinners: 0,
        avgPipsLosers: 0,
        avgTicksPerTrade: 0,
        avgTicksWinners: 0,
        avgTicksLosers: 0,
        riskRewardRatio: 0,
      };
    }

    // Helper function to get profit/loss dollar amount based on equity type
    // profitLossDollar is GROSS (before fees)
    const getProfitLoss = (trade) => {
      const pcProfit = parseFloat(trade.profitLossDollarPersonal || 0);
      const icProfit = parseFloat(trade.profitLossDollarInvestor || 0);
      const grossProfit = pcProfit + icProfit;

      // If showing NET, use netDollarPC and netDollarIC from DB
      if (!showGross) {
        const pcNet = trade.netDollarPC || 0;
        const icNet = trade.netDollarIC || 0;
        return pcNet + icNet;
      }

      return grossProfit;
    };

    // Get outcome (Win/Loss) based on equity type
    const getOutcome = (trade) => {
      if (trade.outcomePC && trade.outcomePC !== "") return trade.outcomePC;
      if (trade.outcomeIC && trade.outcomeIC !== "") return trade.outcomeIC;
      return "";
    };

    const winningTrades = trades.filter(t => getOutcome(t) === "Win");
    const losingTrades = trades.filter(t => getOutcome(t) === "Loss");
    const longTrades = trades.filter(t => t.direction === "Long");
    const shortTrades = trades.filter(t => t.direction === "Short");
    
    const longWinningTrades = longTrades.filter(t => getOutcome(t) === "Win");
    const shortWinningTrades = shortTrades.filter(t => getOutcome(t) === "Win");
    
    const completedTrades = winningTrades.concat(losingTrades); // Exclude breakeven

    // Calculate total profit and loss (respects gross/net toggle via getProfitLoss)
    const totalProfit = winningTrades.reduce((sum, t) => sum + getProfitLoss(t), 0);
    const totalLoss = Math.abs(losingTrades.reduce((sum, t) => sum + getProfitLoss(t), 0));

    const avgWin = winningTrades.length > 0 
      ? totalProfit / winningTrades.length 
      : 0;
    
    const avgLoss = losingTrades.length > 0 
      ? totalLoss / losingTrades.length 
      : 0;

    // Calculate Average RRR (using dollar amounts)
    const avgRRR = avgLoss !== 0 ? avgWin / avgLoss : 0;
    const profitFactor = totalLoss !== 0 ? totalProfit / totalLoss : 0;

    const allProfitLosses = trades.map(t => getProfitLoss(t));
    const bestTrade = trades.length > 0 ? Math.max(...allProfitLosses) : 0;
    const worstTrade = trades.length > 0 ? Math.min(...allProfitLosses) : 0;

    const winRate = completedTrades.length > 0 ? (winningTrades.length / completedTrades.length) * 100 : 0;
    const lossRate = completedTrades.length > 0 ? (losingTrades.length / completedTrades.length) * 100 : 0;
    
    const longWonRate = longTrades.length > 0 ? (longWinningTrades.length / longTrades.length) * 100 : 0;
    const shortWonRate = shortTrades.length > 0 ? (shortWinningTrades.length / shortTrades.length) * 100 : 0;

    // Calculate Max Drawdown and Peak Balance
    // Get starting balance from user data
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const selectedAccountId = localStorage.getItem("selected-account-id") || "All";
    
    // Check if we're in backtesting mode by looking at the trades
    const isBacktesting = trades.length > 0 && trades[0].equityType === "Backtest";
    
    let startingBalance = 0;
    
    if (isBacktesting) {
      // Use backtest starting balance
      startingBalance = getBacktestBalance();
    } else if (selectedAccountId === "All") {
      // Use combined starting balance
      startingBalance = (parseFloat(user.startingBalancePC) || 0) + (parseFloat(user.startingBalanceIC) || 0);
    } else if (selectedAccountId.startsWith("pc-") || selectedAccountId === "All Personal") {
      startingBalance = parseFloat(user.startingBalancePC) || 0;
    } else if (selectedAccountId.startsWith("ic-") || selectedAccountId === "All Investor") {
      startingBalance = parseFloat(user.startingBalanceIC) || 0;
    }

    // Calculate profitability as percentage return on starting balance
    const totalReturn = totalProfit - totalLoss;
    const profitability = startingBalance !== 0 ? (totalReturn / startingBalance) * 100 : 0;
    
    // Calculate drawdown and peak balance by simulating account balance progression
    // This works for both live and backtest modes since getProfitLoss returns individual trade P/L
    let runningBalance = startingBalance;
    let peakBalance = startingBalance;
    let maxDrawdown = 0;
    let maxDrawdownPercent = 0;
    
    trades.forEach(trade => {
      runningBalance += getProfitLoss(trade);
      if (runningBalance > peakBalance) {
        peakBalance = runningBalance;
      }
      const drawdown = peakBalance - runningBalance;
      if (drawdown > maxDrawdown) {
        maxDrawdown = drawdown;
        maxDrawdownPercent = peakBalance !== 0 ? (drawdown / peakBalance) * 100 : 0;
      }
    });

    // Separate forex and non-forex trades for pips/ticks calculations
    const forexTrades = trades.filter(trade => {
      const pairObject = markets.find(m => m.ticker === trade.pair);
      return pairObject?.multiplier === 1;
    });
    
    const nonForexTrades = trades.filter(trade => {
      const pairObject = markets.find(m => m.ticker === trade.pair);
      return pairObject?.multiplier !== 1;
    });

    const forexWinningTrades = forexTrades.filter(t => getOutcome(t) === "Win");
    const forexLosingTrades = forexTrades.filter(t => getOutcome(t) === "Loss");
    const nonForexWinningTrades = nonForexTrades.filter(t => getOutcome(t) === "Win");
    const nonForexLosingTrades = nonForexTrades.filter(t => getOutcome(t) === "Loss");

    // Calculate Pip metrics (forex only) - using actual price movement
    const getPips = (trade) => {
      // Use Personal Capital prices first, fallback to Investor Capital
      const openPrice = parseFloat(trade.openPricePC || trade.openPriceIC || 0);
      const closePrice = parseFloat(trade.closePricePC || trade.closePriceIC || 0);
      const pairObject = markets.find(m => m.ticker === trade.pair);
      const pipDistance = pairObject?.pipDistance || 0.0001;
      
      if (openPrice === 0 || closePrice === 0) return 0;
      
      const priceMovement = (closePrice - openPrice) / pipDistance;
      
      // For long trades: positive movement = win, negative = loss
      // For short trades: negative movement = win, positive = loss
      const direction = trade.direction;
      if (direction === "Long") {
        return priceMovement;
      } else if (direction === "Short") {
        return -priceMovement;
      }
      return 0;
    };
    
    // Calculate Tick metrics (non-forex only) - using actual price movement
    const getTicks = (trade) => {
      // Use Personal Capital prices first, fallback to Investor Capital
      const openPrice = parseFloat(trade.openPricePC || trade.openPriceIC || 0);
      const closePrice = parseFloat(trade.closePricePC || trade.closePriceIC || 0);
      const pairObject = markets.find(m => m.ticker === trade.pair);
      // For non-forex, use pipDistance as tickSize since tickSize might not be in markets data
      const tickSize = pairObject?.pipDistance || 0.01;
      
      if (openPrice === 0 || closePrice === 0) return 0;
      
      const priceMovement = (closePrice - openPrice) / tickSize;
      
      // For long trades: positive movement = win, negative = loss
      // For short trades: negative movement = win, positive = loss
      const direction = trade.direction;
      if (direction === "Long") {
        return priceMovement;
      } else if (direction === "Short") {
        return -priceMovement;
      }
      return 0;
    };
    
    // Pips calculations (forex trades)
    const totalPips = forexTrades.reduce((sum, t) => sum + getPips(t), 0);
    const avgPipsPerTrade = forexTrades.length > 0 ? totalPips / forexTrades.length : 0;
    
    const winningPips = forexWinningTrades.reduce((sum, t) => sum + getPips(t), 0);
    const avgPipsWinners = forexWinningTrades.length > 0 ? winningPips / forexWinningTrades.length : 0;
    
    const losingPips = forexLosingTrades.reduce((sum, t) => sum + getPips(t), 0);
    const avgPipsLosers = forexLosingTrades.length > 0 ? losingPips / forexLosingTrades.length : 0;

    // Ticks calculations (non-forex trades)
    const totalTicks = nonForexTrades.reduce((sum, t) => sum + getTicks(t), 0);
    const avgTicksPerTrade = nonForexTrades.length > 0 ? totalTicks / nonForexTrades.length : 0;
    
    const winningTicks = nonForexWinningTrades.reduce((sum, t) => sum + getTicks(t), 0);
    const avgTicksWinners = nonForexWinningTrades.length > 0 ? winningTicks / nonForexWinningTrades.length : 0;
    
    const losingTicks = nonForexLosingTrades.reduce((sum, t) => sum + getTicks(t), 0);
    const avgTicksLosers = nonForexLosingTrades.length > 0 ? losingTicks / nonForexLosingTrades.length : 0;

    // Calculate Risk-Reward Ratio (separate for forex and non-forex)
    const forexRiskRewardRatio = avgPipsLosers !== 0 ? Math.abs(avgPipsWinners / avgPipsLosers) : 0;
    const nonForexRiskRewardRatio = avgTicksLosers !== 0 ? Math.abs(avgTicksWinners / avgTicksLosers) : 0;
    
    // Use the appropriate risk-reward ratio based on which type of trades we have
    let riskRewardRatio = 0;
    if (forexTrades.length > 0 && nonForexTrades.length > 0) {
      // If both types exist, use weighted average based on trade count
      riskRewardRatio = (forexRiskRewardRatio * forexTrades.length + nonForexRiskRewardRatio * nonForexTrades.length) / 
                       (forexTrades.length + nonForexTrades.length);
    } else if (forexTrades.length > 0) {
      riskRewardRatio = forexRiskRewardRatio;
    } else if (nonForexTrades.length > 0) {
      riskRewardRatio = nonForexRiskRewardRatio;
    }

    return {
      profitability,
      avgWinningTrade: avgWin,
      avgLosingTrade: -avgLoss, // Make negative for display
      totalTrades: trades.length,
      avgRRR,
      winRate,
      lossRate,
      profitFactor,
      bestTrade,
      worstTrade,
      longWon: longWonRate,
      shortWon: shortWonRate,
      grossProfit: totalProfit, // Actually respects gross/net toggle
      grossLoss: -totalLoss, // Make negative for display, respects gross/net toggle
      peakBalance, // Highest balance reached, respects gross/net toggle
      maxDrawdown: -maxDrawdown, // Make negative for display, respects gross/net toggle
      maxDrawdownPercent, // Respects gross/net toggle
      avgPipsPerTrade,
      avgPipsWinners,
      avgPipsLosers,
      avgTicksPerTrade,
      avgTicksWinners,
      avgTicksLosers,
      riskRewardRatio,
    };
  }, [trades, showGross]);

  const isDark = theme[0] === "dark";

  const StatItem = ({ label, value, isPercentage = false, isCurrency = false, showBar = false, isPipsTicks = false, pipsValue = null, ticksValue = null }) => (
    <div style={{ marginBottom: "12px" }}>
      <div style={{
        fontSize: "13px",
        color: isDark ? "#9ca3af" : "#6b7280",
        marginBottom: "4px",
        fontWeight: "400",
      }}>
        {label}
      </div>
      {showBar ? (
        <div>
          <div style={{
            fontSize: "14px",
            fontWeight: "600",
            color: value >= 0 ? (isDark ? "#10b981" : "#059669") : (isDark ? "#ef4444" : "#dc2626"),
            marginBottom: "6px",
          }}>
            {value >= 0 && "+"}{value.toFixed(2)}%
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{
              flex: 1,
              height: "8px",
              backgroundColor: isDark ? "#2d2e33" : "#e5e7eb",
              borderRadius: "4px",
              overflow: "hidden",
            }}>
              <div style={{
                width: `${Math.min(Math.abs(value), 100)}%`,
                height: "100%",
                background: value >= 0 
                  ? "linear-gradient(90deg, #10b981 0%, #059669 100%)"
                  : "linear-gradient(90deg, #ef4444 0%, #dc2626 100%)",
                transition: "width 0.3s ease",
              }} />
            </div>
          </div>
        </div>
      ) : (
        <div style={{
          fontSize: "14px",
          fontWeight: "600",
          color: isCurrency 
            ? (value >= 0 ? (isDark ? "#10b981" : "#059669") : (isDark ? "#ef4444" : "#dc2626"))
            : (isDark ? "#e5e7eb" : "#374151"),
        }}>
          {isPipsTicks ? (
            <div>
              {pipsValue !== null && `Pips: ${pipsValue.toFixed(2)}`}
              {pipsValue !== null && ticksValue !== null && " | "}
              {ticksValue !== null && `Ticks: ${ticksValue.toFixed(2)}`}
            </div>
          ) : (
            <>
              {isCurrency && value >= 0 && "+"}
              {isCurrency && "$"}
              {isCurrency ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : value.toFixed(2)}
              {isPercentage && "%"}
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      width: "100%",
      padding: "20px",
      backgroundColor: isDark ? "#1e1f23" : "#ffffff",
      borderRadius: "12px",
      border: `1px solid ${isDark ? "#2d2e33" : "#e5e7eb"}`,
      marginTop: "16px",
    }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
        gap: "24px",
      }}>
        {/* Column 1 - Performance */}
        <div>
          <StatItem label="Profitability" value={stats.profitability} showBar />
          <StatItem label="Avg. Winning Trade" value={stats.avgWinningTrade} isCurrency />
          <StatItem label="Avg. Losing Trade" value={stats.avgLosingTrade} isCurrency />
          <StatItem label="Trades" value={stats.totalTrades} />
          <StatItem label="Avg. RRR" value={stats.avgRRR} />
        </div>

        {/* Column 2 - Win/Loss Stats */}
        <div>
          <StatItem label="Win Rate" value={stats.winRate} isPercentage />
          <StatItem label="Loss Rate" value={stats.lossRate} isPercentage />
          <StatItem label="Long Won" value={stats.longWon} isPercentage />
          <StatItem label="Short Won" value={stats.shortWon} isPercentage />
          <StatItem label="Profit Factor" value={stats.profitFactor} />
        </div>

        {/* Column 3 - P&L */}
        <div>
          <StatItem label="Gross Profit" value={stats.grossProfit} isCurrency />
          <StatItem label="Gross Loss" value={stats.grossLoss} isCurrency />
          <StatItem label="Best Trade" value={stats.bestTrade} isCurrency />
          <StatItem label="Worst Trade" value={stats.worstTrade} isCurrency />
          <StatItem label="Peak Balance" value={stats.peakBalance} isCurrency />
        </div>

        {/* Column 4 - Risk & Pips/Ticks */}
        <div>
          <StatItem label="Max Drawdown ($)" value={stats.maxDrawdown} isCurrency />
          <StatItem label="Max Drawdown (%)" value={stats.maxDrawdownPercent} isPercentage />
          <StatItem label="Avg. Pips/Ticks/Trade" isPipsTicks pipsValue={stats.avgPipsPerTrade} ticksValue={stats.avgTicksPerTrade} />
          <StatItem label="Avg. Pips/Ticks (Winners)" isPipsTicks pipsValue={stats.avgPipsWinners} ticksValue={stats.avgTicksWinners} />
          <StatItem label="Avg. Pips/Ticks (Losers)" isPipsTicks pipsValue={stats.avgPipsLosers} ticksValue={stats.avgTicksLosers} />
          <StatItem label="Risk-Reward Ratio" value={stats.riskRewardRatio} />
        </div>
      </div>
    </div>
  );
};

export default TradingStatisticsPanel;
